import moment from 'moment-timezone';
import { apiService } from './ApiService';
import { connectionService } from './ConnectionService';
import Order from '../models/Order';
import { I18n } from '../wrappers/I18n';
import { dateFormatter } from '../utilities/dateFormatter';
import { OrderFlagType } from '../types';
const { getTimezone } = dateFormatter();
/**
 * Which link puts a receipt under another, best first. A closing hangs under the final it names (not
 * under its advance); a storno or correction under what it corrects; a final under its advance or
 * proforma; an e-invoice or copy under its receipt; a round under the bill that paid it.
 */
const PARENT_RANK = {
    closingFor: 0,
    cancels: 1,
    corrects: 1,
    fromAdvance: 2,
    fromProforma: 3,
    einvoiceFor: 4,
    copyOf: 5,
    paidBy: 6,
    reducesAdvance: 7,
};
/** The links a receipt has to several advances at once: those advances stand together above it. */
const TOGETHER = ['reducesAdvance', 'fromAdvance'];
/** How far a family is followed, and how many receipts it holds: an advance used many times, a bill paying many rounds. */
const MAX_DEPTH = 5;
const MAX_NODES = 80;
export const ReceiptLinkService = {
    /** An advance known only by the reference a Serbian linked refund holds: it has no receipt to open. */
    isReferenceOnly,
    /**
     * Serbia: the live linked refunds of `advance` (advances at a negative amount that name it by its fiscal
     * reference), read from its links (`links`). The advance's own rest doesn't count them.
     */
    liveLinkedRefunds(advance, links) {
        // A refund cancelled here is known so in this register's store before the advance's copy of it is fresh.
        return links.filter(link => link.kind === 'reducedBy' && !link.order.canceled()
            && !Order.find(link.order.hash)?.canceled()
            && namedAdvance(link.order)?.number === advance.register_number)
            .map(link => link.order);
    },
    /** What is left of an advance: its own rest (after its closings), less its live linked refunds (Serbia), which that rest doesn't count. */
    advanceLeft(advance, liveRefunds) {
        return liveRefunds.reduce((rest, refund) => rest.add(refund.getToPay()), advance.getRemainingAdvanceAmount());
    },
    /** What is left of a family's advance (advanceLeft over its links). */
    advanceRest(node) {
        return ReceiptLinkService.advanceLeft(node.order, ReceiptLinkService.liveLinkedRefunds(node.order, node.links));
    },
    /**
     * Every link `order` carries, in both directions. `others` are receipts beside this register's store
     * (the receipts list loaded) where a Serbian linked refund of an advance may be found.
     */
    links(order, others = []) {
        const seen = new Set();
        const links = [];
        const add = (other, outgoing) => {
            const type = other.reference?.type ?? '';
            const kind = kindOf(order, other, type, outgoing);
            const key = `${other.hash}:${kind}`;
            if (!kind || seen.has(key))
                return;
            seen.add(key);
            links.push({ kind, order: other, type, outgoing });
        };
        order.refers_to.forEach(other => add(other, true));
        order.referred_by.forEach(other => add(other, false));
        // Serbia: a linked refund issued before refunds kept their link names its advance only by the fiscal
        // reference; one with its link is read above.
        const push = (other, kind, outgoing) => {
            if (seen.has(`${other.hash}:${kind}`))
                return;
            seen.add(`${other.hash}:${kind}`);
            links.push({ kind, order: other, type: 'linkedRefund', outgoing });
        };
        const named = order.refers_to.some(other => other.reference?.type === 'linkedRefund') ? null : namedAdvance(order);
        if (named)
            push(heldOrLoaded(others).find(other => other.isAdvance() && other.register_number === named.number) ?? referenceOnly(named), 'reducesAdvance', true);
        linkedRefunds(order, others).forEach(refund => push(refund, 'reducedBy', false));
        return links;
    },
    /**
     * The family of `start`: every receipt linked to it, directly or through others, up to MAX_DEPTH
     * links away. A linked receipt is read from this register's store, else from the server; offline,
     * one this register doesn't hold stays its one-level copy and its own links are not followed.
     */
    async family(start, others = []) {
        const nodes = new Map([[start.hash, newNode(start, true)]]);
        const known = () => [...others, ...[...nodes.values()].map(node => node.order)];
        let layer = [...nodes.values()];
        for (let depth = 0; depth < MAX_DEPTH && layer.length; depth++) {
            const next = [];
            for (const node of layer) {
                if (!node.complete)
                    continue;
                for (const link of ReceiptLinkService.links(node.order, known())) {
                    if (nodes.has(link.order.hash) || nodes.size >= MAX_NODES)
                        continue;
                    const found = newNode(link.order, false);
                    nodes.set(link.order.hash, found);
                    next.push(found);
                }
            }
            await complete(next);
            // An advance found by its fiscal reference takes its own key; one already in the family is not repeated.
            for (const node of [...next]) {
                const key = [...nodes].find(([, held]) => held === node)[0];
                if (key === node.order.hash)
                    continue;
                nodes.delete(key);
                if (nodes.has(node.order.hash))
                    next.splice(next.indexOf(node), 1);
                else
                    nodes.set(node.order.hash, node);
            }
            layer = next;
        }
        // Each reference once, read from the receipt that holds it when that one is complete: a closing
        // knows the final it names, while the advance's copy of it only shows a storno.
        const edges = new Map();
        for (const node of nodes.values()) {
            if (!node.complete)
                continue;
            for (const link of ReceiptLinkService.links(node.order, known())) {
                if (!nodes.has(link.order.hash))
                    continue;
                const from = link.outgoing ? node.order.hash : link.order.hash;
                const to = link.outgoing ? link.order.hash : node.order.hash;
                const key = `${from}|${to}|${link.type}`;
                if (link.outgoing || !edges.has(key)) {
                    edges.set(key, { from, to, kind: link.outgoing ? link.kind : reverse(link.kind) });
                }
            }
        }
        for (const { from, to, kind } of edges.values()) {
            const referrer = nodes.get(from);
            const referred = nodes.get(to);
            referrer.links.push({ kind, order: referred.order });
            referred.links.push({ kind: reverse(kind), order: referrer.order });
        }
        for (const node of nodes.values()) {
            // A bill cancelled and the table billed again: a round hangs under the bill that pays it now.
            const payer = node.order.paidByOrder()?.hash;
            const paysNow = (link) => link.kind === 'paidBy' && link.order.hash === payer;
            const candidates = node.links
                .filter(link => PARENT_RANK[link.kind] !== undefined)
                .sort((a, b) => PARENT_RANK[a.kind] - PARENT_RANK[b.kind]
                || Number(paysNow(b)) - Number(paysNow(a))
                || byDate(a.order, b.order));
            for (const candidate of candidates) {
                const parent = nodes.get(candidate.order.hash);
                if (isAbove(node, parent))
                    continue;
                node.parent = parent;
                node.kind = candidate.kind;
                parent.children.push(node);
                break;
            }
        }
        // A receipt that came from several advances at once (a refund closing them, a final paid from them)
        // hangs under the first; the other advances stand with it.
        // A live receipt groups first: a refund cancelled and redone over another set leaves the redo's set.
        const grouped = new Set();
        for (const node of [...nodes.values()].sort((a, b) => Number(a.order.canceled()) - Number(b.order.canceled()))) {
            const parent = node.parent;
            if (!parent || parent.parent || grouped.has(parent) || !TOGETHER.includes(node.kind))
                continue;
            for (const link of node.links) {
                const other = nodes.get(link.order.hash);
                if (link.kind !== node.kind || other === parent || other.parent || other.together.length || grouped.has(other))
                    continue;
                parent.together.push(other);
                grouped.add(other);
            }
        }
        const all = [...nodes.values()];
        all.forEach(node => {
            node.children.sort((a, b) => byDate(a.order, b.order));
            node.together.sort((a, b) => byDate(a.order, b.order));
        });
        return {
            origins: all.filter(node => !node.parent && !grouped.has(node)).sort((a, b) => byDate(a.order, b.order)),
            nodes: all,
        };
    },
    /** `order` as this register stores it, else as the server holds it, else as given. */
    async fresh(order) {
        const node = newNode(order, false);
        await complete([node]);
        return node.order;
    },
};
function newNode(order, complete) {
    return { order, kind: null, parent: null, children: [], together: [], links: [], complete };
}
/** Whether `node` is `other` or one of the receipts above it: hanging `node` under it would close a loop. */
function isAbove(node, other) {
    for (let at = other; at; at = at.parent) {
        if (at === node)
            return true;
    }
    return false;
}
function byDate(a, b) {
    return (a.last_activity_at ?? '').localeCompare(b.last_activity_at ?? '') || (a.number ?? 0) - (b.number ?? 0);
}
function kindOf(shown, other, type, outgoing) {
    switch (type) {
        case 'corrective': {
            const corrector = outgoing ? shown : other;
            if (isAdvanceClosing(corrector))
                return outgoing ? 'reducesAdvance' : 'reducedBy';
            if (corrector.correctsPartly())
                return outgoing ? 'corrects' : 'correctedBy';
            return outgoing ? 'cancels' : 'cancelledBy';
        }
        case 'advanceFinal': return outgoing ? 'closingFor' : 'closedBy';
        case 'linkedRefund': return outgoing ? 'reducesAdvance' : 'reducedBy';
        case 'advance': return outgoing ? 'fromAdvance' : 'usedByFinal';
        case 'proforma': return outgoing ? 'fromProforma' : 'proformaFinal';
        case 'summary': return outgoing ? 'pays' : 'paidBy';
        case 'copy': return outgoing ? 'copyOf' : 'copiedAs';
        case 'einvoice': return outgoing ? 'einvoiceFor' : 'einvoicedAs';
        default: return null;
    }
}
/**
 * A closing takes the used part off an advance: it names its final, or (seen from the advance, as a
 * one-level copy) carries the `type,closeAdvance` flag. Its undo carries that flag too, but is a storno.
 */
function isAdvanceClosing(order) {
    if (order.refers_to.some(ref => ref.reference?.type === 'advanceFinal'))
        return true;
    return order.getFlag(OrderFlagType.type)[0] === 'closeAdvance'
        && !['cancel', 'cancelSummary'].includes(order.getFlag(OrderFlagType.correctType)[0]);
}
const REVERSE = {
    cancels: 'cancelledBy', cancelledBy: 'cancels',
    corrects: 'correctedBy', correctedBy: 'corrects',
    reducesAdvance: 'reducedBy', reducedBy: 'reducesAdvance',
    closingFor: 'closedBy', closedBy: 'closingFor',
    fromAdvance: 'usedByFinal', usedByFinal: 'fromAdvance',
    fromProforma: 'proformaFinal', proformaFinal: 'fromProforma',
    pays: 'paidBy', paidBy: 'pays',
    copyOf: 'copiedAs', copiedAs: 'copyOf',
    einvoiceFor: 'einvoicedAs', einvoicedAs: 'einvoiceFor',
};
function reverse(kind) {
    return REVERSE[kind];
}
/** Replace one-level copies with the full receipts: this register's store first, then one server lookup. */
async function complete(nodes) {
    const missing = [];
    for (const node of nodes) {
        const stored = Order.find(node.order.hash);
        if (stored) {
            node.order = stored;
            node.complete = true;
        }
        else {
            missing.push(node);
        }
    }
    if (!connectionService.isOnline)
        return;
    await Promise.all(missing.filter(node => isReferenceOnly(node.order)).map(findByReference));
    // An advance found by its reference came whole from the search.
    const ids = missing.filter(node => !node.complete).map(node => node.order.id).filter((id) => !!id);
    if (!ids.length)
        return;
    try {
        const fetched = await fetchOrders(ids);
        for (const node of missing) {
            const data = fetched.find(order => order.hash === node.order.hash);
            if (!data)
                continue;
            node.order = new Order(data);
            node.complete = true;
        }
    }
    catch {
        // The one-level copies stand: the family still shows them, without their own links.
    }
}
/**
 * Serbia: the advance a linked refund (an advance at a negative amount) names by its fiscal reference, the
 * `ref` flag: the advance's PFR number and, when the POS made the link, its registration time.
 */
function namedAdvance(order) {
    // The flag first: it runs over the whole store for every advance row of Računi.
    const [number, at] = order.getFlag(OrderFlagType.ref);
    if (!number || !order.isAdvance() || !I18n.c('order.allowAdvanceLinked') || order.getToPay().toUnit() >= 0)
        return null;
    return { number, at: at ?? '' };
}
/** The linked refunds that name `advance`, among this register's store and `others`. */
function linkedRefunds(advance, others) {
    if (!I18n.c('order.allowAdvanceLinked') || !advance.isAdvance() || !advance.register_number)
        return [];
    const found = new Map();
    for (const other of heldOrLoaded(others)) {
        if (other.hash !== advance.hash && namedAdvance(other)?.number === advance.register_number)
            found.set(other.hash, other);
    }
    return [...found.values()];
}
function heldOrLoaded(others) {
    return [...Order.all(), ...others];
}
/** An advance known only by the reference that names it: shown, not openable, until it is found. */
function referenceOnly({ number, at }) {
    return new Order({
        hash: `ref:${number}`, status: 'advance', register_number: number,
        last_activity_at: at ? moment.tz(at, getTimezone()).utc().format('YYYY-MM-DD HH:mm:ss') : null,
    });
}
function isReferenceOnly(order) {
    return order.hash.startsWith('ref:');
}
/** One search for the PFR number, on the day the advance was registered (the date narrows it: the number has no index). */
async function findByReference(node) {
    if (!node.order.last_activity_at)
        return;
    const day = moment.utc(node.order.last_activity_at).tz(getTimezone()).format('YYYY-MM-DD');
    try {
        const response = await apiService.get('/orders', { params: {
                filter: node.order.register_number, startDate: `${day} 00:00:00`, endDate: `${day} 23:59:59`, page: 1, itemsPerPage: 20,
            } });
        const found = response.data.data.find(order => order.register_number === node.order.register_number);
        if (!found)
            return;
        node.order = new Order(found);
        node.complete = true;
    }
    catch {
        // The reference stands: the advance shows by its PFR number.
    }
}
/** Orders by id as the server holds them, with their items and references. */
async function fetchOrders(ids) {
    const response = await apiService.get('/orders/issue-result', { params: { order_ids: ids } });
    return response.data.orders;
}
export default ReceiptLinkService;
//# sourceMappingURL=ReceiptLinkService.js.map